/**
 * Vervit — sync wishlist + cart across devices for the same phone/email.
 * Merges local browser data with Cloudflare KV via POST /user-sync.
 * Deletion tombstones prevent deleted items from being re-added by remote sync.
 */
(function () {
  'use strict';

  const DEBOUNCE_MS = 900;
  const RETURN_SYNC_MIN_MS = 2 * 1000; // 2s between tab return checks
  const WISHLIST_TOMBSTONE_KEY = 'vrv_wishlist_deleted_v1';
  const CART_TOMBSTONE_KEY = 'vrv_cart_deleted_v1';
  const WISHLIST_CLEARED_KEY = 'vrv_wishlist_cleared_v1';
  const CART_CLEARED_KEY = 'vrv_cart_cleared_v1';
  const ACTIVE_IDENTITY_KEY = 'vrv_active_sync_identity_v1';
  const GUEST_CART_SNAPSHOT_KEY = 'vrv_guest_cart_snapshot_v1';
  const TOMBSTONE_TTL_MS = 30 * 24 * 60 * 60 * 1000; // 30 days
  // Written by cart-drawer-extras.js while a Buy Now checkout has the shopper's
  // real cart swapped out for the single Buy Now product (this tab only).
  const BUY_NOW_SAVED_CART_KEY = 'vrv_buy_now_saved_cart_v1';

  // While the swap is pending the Shopify cart is NOT the shopper's cart. Syncing
  // then would push that temporary cart over the server copy, or merge server
  // items into it. Sync resumes as soon as the real cart is restored.
  function isBuyNowCartSwapPending() {
    try {
      return sessionStorage.getItem(BUY_NOW_SAVED_CART_KEY) !== null;
    } catch (_) {
      return false;
    }
  }

  function discardBuyNowSavedCart() {
    try {
      sessionStorage.removeItem(BUY_NOW_SAVED_CART_KEY);
      localStorage.removeItem('vrv_buy_now_backup_v1');
    } catch (_) {}
  }

  let pushTimer = null;
  let syncInFlight = null;
  let applyingRemote = false;
  let localMutationGen = 0;
  let lastLocalCartMutationAt = 0;

  function isCustomerLoggedIn() {
    const body = document.body;
    if (!body) return false;
    return (
      body.getAttribute('data-vrv-customer-logged-in') === '1' ||
      body.classList.contains('customer-logged-in')
    );
  }

  function readGuestCartSnapshot() {
    try {
      const raw = JSON.parse(localStorage.getItem(GUEST_CART_SNAPSHOT_KEY) || '[]');
      return Array.isArray(raw) ? raw : [];
    } catch (_) {
      return [];
    }
  }

  function writeGuestCartSnapshot(items) {
    try {
      if (Array.isArray(items) && items.length > 0) {
        localStorage.setItem(GUEST_CART_SNAPSHOT_KEY, JSON.stringify(items));
      } else {
        localStorage.removeItem(GUEST_CART_SNAPSHOT_KEY);
      }
    } catch (_) {}
  }

  function clearGuestCartSnapshot() {
    try {
      localStorage.removeItem(GUEST_CART_SNAPSHOT_KEY);
    } catch (_) {}
  }

  function updateGuestCartSnapshotFromCart(cartData) {
    if (isCustomerLoggedIn()) return;
    const rawItems = cartData && Array.isArray(cartData.items) ? cartData.items : [];
    const items = rawItems
      .map(function (it) {
        const vid = Number(it.variant_id || it.variantId || it.id);
        const qty = Math.max(1, Number(it.quantity) || 1);
        if (!vid || Number.isNaN(vid)) return null;
        return { variant_id: vid, quantity: qty };
      })
      .filter(Boolean);
    writeGuestCartSnapshot(items);
  }

  // ─── Tombstone helpers ─────────────────────────────────────────────────────
  // Tombstones record intentional deletions so the server cannot re-add them.

  function readWishlistTombstones() {
    try {
      const raw = JSON.parse(localStorage.getItem(WISHLIST_TOMBSTONE_KEY) || '{}');
      return typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    } catch (_) { return {}; }
  }

  function readCartTombstones() {
    try {
      const raw = JSON.parse(localStorage.getItem(CART_TOMBSTONE_KEY) || '{}');
      return typeof raw === 'object' && !Array.isArray(raw) ? raw : {};
    } catch (_) { return {}; }
  }

  function pruneTombstones(obj) {
    const now = Date.now();
    const out = {};
    for (const k of Object.keys(obj)) {
      if (now - Number(obj[k]) < TOMBSTONE_TTL_MS) out[k] = obj[k];
    }
    return out;
  }

  function isWishlistUserCleared() {
    try {
      return Boolean(localStorage.getItem(WISHLIST_CLEARED_KEY));
    } catch (_) {
      return false;
    }
  }

  function isCartUserCleared() {
    try {
      return Boolean(localStorage.getItem(CART_CLEARED_KEY));
    } catch (_) {
      return false;
    }
  }

  function markWishlistUserCleared() {
    try {
      localStorage.setItem(WISHLIST_CLEARED_KEY, String(Date.now()));
    } catch (_) {}
  }

  function markCartUserCleared() {
    try {
      localStorage.setItem(CART_CLEARED_KEY, String(Date.now()));
    } catch (_) {}
  }

  function clearWishlistUserClearedFlag() {
    try {
      localStorage.removeItem(WISHLIST_CLEARED_KEY);
    } catch (_) {}
  }

  function clearCartUserClearedFlag() {
    try {
      localStorage.removeItem(CART_CLEARED_KEY);
    } catch (_) {}
  }

  function readRawLocalWishlist() {
    try {
      const raw = JSON.parse(localStorage.getItem('wishlist') || '[]');
      if (!Array.isArray(raw)) return [];
      return raw.filter((item) => item && typeof item === 'object' && item.handle);
    } catch (_) {
      return [];
    }
  }

  function purgeGhostWishlistFromStorage() {
    try {
      const items = readRawLocalWishlist();
      clearWishlistTombstonesForPresentItems(items);
      const tombstones = readWishlistTombstones();
      const cleaned = filterWishlistByTombstones(items, tombstones);
      if (JSON.stringify(items) !== JSON.stringify(cleaned)) {
        localStorage.setItem('wishlist', JSON.stringify(cleaned));
      }
      if (!cleaned.length && Object.keys(tombstones).length) {
        markWishlistUserCleared();
      }
    } catch (_) {}
  }

  function buildSyncPayload(identity) {
    const rawWishlist = readRawLocalWishlist();
    clearWishlistTombstonesForPresentItems(rawWishlist);
    let wishlist = readLocalWishlist();
    let cartItems = [];
    const wishlistTombstones = pruneTombstones(readWishlistTombstones());
    const cartTombstones = pruneTombstones(readCartTombstones());
    wishlist = filterWishlistByTombstones(wishlist, wishlistTombstones);
    cartItems = filterCartItemsByTombstones(cartItems, cartTombstones);

    const deletedWishlistHandles = Object.keys(wishlistTombstones);
    const deletedCartVariantIds = Object.keys(cartTombstones)
      .map(Number)
      .filter(function (n) { return n > 0 && !isNaN(n); });

    const wishlistCleared = !wishlist.length && (isWishlistUserCleared() || deletedWishlistHandles.length > 0);
    const cartCleared = !cartItems.length && (isCartUserCleared() || deletedCartVariantIds.length > 0);

    return {
      action: 'sync',
      phone: identity.phone,
      email: identity.email,
      wishlist: wishlist,
      cart: { items: cartItems },
      deleted_wishlist_handles: deletedWishlistHandles,
      deleted_cart_variant_ids: deletedCartVariantIds,
      wishlist_cleared: wishlistCleared,
      cart_cleared: cartCleared,
      updatedAt: Date.now(),
    };
  }

  async function buildSyncPayloadAsync(identity, isLoginMerge, isPush) {
    const payload = buildSyncPayload(identity);
    let localItems = [];
    try {
      localItems = await fetchLocalCartItems();
    } catch (_) {}

    // Safety net: if local cart in Shopify is temporarily empty but guest snapshot had items,
    // ensure guest snapshot items are included in sync payload for login merge
    if (!localItems.length && isLoginMerge) {
      const snapshot = readGuestCartSnapshot();
      if (snapshot.length) {
        localItems = snapshot.map(function (it) {
          return {
            variant_id: Number(it.variant_id || it.variantId),
            quantity: Math.max(1, Number(it.quantity) || 1),
          };
        });
      }
    }

    clearWishlistTombstonesForPresentItems(readRawLocalWishlist());
    clearCartTombstonesForPresentItems(localItems);
    payload.cart.items = filterCartItemsByTombstones(
      localItems,
      pruneTombstones(readCartTombstones())
    );
    const cartTombstones = pruneTombstones(readCartTombstones());
    const deletedCartVariantIds = Object.keys(cartTombstones)
      .map(Number)
      .filter(function (n) { return n > 0 && !isNaN(n); });
    payload.deleted_cart_variant_ids = deletedCartVariantIds;
    payload.cart_updated_at = lastLocalCartMutationAt || Date.now();

    if (isLoginMerge) {
      payload.action = 'sync';
      payload.is_login_merge = true;
      payload.cart_cleared = false;
    } else if (isPush) {
      payload.action = 'push';
      payload.replace_cart = true;
      payload.cart_cleared =
        !payload.cart.items.length && (isCartUserCleared() || deletedCartVariantIds.length > 0);
    } else {
      payload.action = 'get';
      payload.cart_cleared =
        !payload.cart.items.length && (isCartUserCleared() || deletedCartVariantIds.length > 0);
    }
    return payload;
  }

  function addWishlistTombstone(handle) {
    if (!handle) return;
    try {
      const t = pruneTombstones(readWishlistTombstones());
      t[String(handle)] = Date.now();
      localStorage.setItem(WISHLIST_TOMBSTONE_KEY, JSON.stringify(t));
      bumpLocalMutationGen();
    } catch (_) {}
  }

  function clearWishlistTombstone(handle) {
    if (!handle) return;
    try {
      const t = pruneTombstones(readWishlistTombstones());
      if (!Object.prototype.hasOwnProperty.call(t, String(handle))) return;
      delete t[String(handle)];
      localStorage.setItem(WISHLIST_TOMBSTONE_KEY, JSON.stringify(t));
      clearWishlistUserClearedFlag();
      bumpLocalMutationGen();
    } catch (_) {}
  }

  function addCartTombstone(variantId) {
    if (!variantId) return;
    try {
      const t = pruneTombstones(readCartTombstones());
      t[String(variantId)] = Date.now();
      localStorage.setItem(CART_TOMBSTONE_KEY, JSON.stringify(t));
      bumpLocalMutationGen();
    } catch (_) {}
  }

  // A tombstone means "the shopper removed this product". If the product is in
  // the live cart right now it was added back, so the tombstone is stale --
  // left alone it hid the re-added product from cross-device sync for 30 days.
  function clearCartTombstonesForPresentItems(items) {
    try {
      const tombstones = readCartTombstones();
      let changed = false;
      (items || []).forEach(function (item) {
        const vid = String(Number(item && (item.variant_id || item.variantId)));
        if (Object.prototype.hasOwnProperty.call(tombstones, vid)) {
          delete tombstones[vid];
          changed = true;
        }
      });
      if (changed) {
        localStorage.setItem(CART_TOMBSTONE_KEY, JSON.stringify(tombstones));
        clearCartUserClearedFlag();
      }
    } catch (_) {}
  }

  function clearWishlistTombstonesForPresentItems(items) {
    try {
      const tombstones = pruneTombstones(readWishlistTombstones());
      let changed = false;
      (items || []).forEach(function (item) {
        const handle = String((item && item.handle) || '').trim();
        if (handle && Object.prototype.hasOwnProperty.call(tombstones, handle)) {
          delete tombstones[handle];
          changed = true;
        }
      });
      if (changed) {
        localStorage.setItem(WISHLIST_TOMBSTONE_KEY, JSON.stringify(tombstones));
        clearWishlistUserClearedFlag();
      }
    } catch (_) {}
  }

  let lastLocalMutationAt = 0;

  function bumpLocalMutationGen() {
    localMutationGen += 1;
    lastLocalMutationAt = Date.now();
  }

  function filterWishlistByTombstones(items, tombstones) {
    const ts = tombstones || readWishlistTombstones();
    return (items || []).filter(function (item) {
      return item && item.handle && !ts[item.handle];
    });
  }

  function filterCartItemsByTombstones(items, tombstones) {
    const ts = tombstones || readCartTombstones();
    return (items || []).filter(function (item) {
      const vid = Number(item.variant_id || item.variantId);
      return vid > 0 && !Number.isNaN(vid) && !ts[String(vid)];
    });
  }

  // Expose globally so wishlist-drawer.js and cart.js can record tombstones
  window.vrvAddWishlistTombstone = addWishlistTombstone;
  window.vrvClearWishlistTombstone = clearWishlistTombstone;
  window.vrvClearWishlistTombstonesForPresentItems = clearWishlistTombstonesForPresentItems;
  window.vrvAddCartTombstone = addCartTombstone;

  // ─── Identity ──────────────────────────────────────────────────────────────

  function getConfig() {
    return window.vrvUserSyncConfig || window.vrvPincodeConfig || {};
  }

  function getApiUrl() {
    const cfg = getConfig();
    if (cfg.userSyncApiUrl) return String(cfg.userSyncApiUrl);
    const spin = cfg.spinWebhookUrl || '';
    if (!spin) return '';
    return String(spin).replace(/\/spin-event\/?$/i, '/user-sync');
  }

  function parseRzpConfig() {
    const el = document.getElementById('RazorpayMagicSSOConfig');
    if (!el) return null;
    try {
      const raw = (el.textContent || el.innerHTML || '').trim();
      return raw ? JSON.parse(raw) : null;
    } catch (_) {
      return null;
    }
  }

  function getStoredPhone() {
    try {
      return localStorage.getItem('vrvCheckoutPhone') || '';
    } catch (_) {
      return '';
    }
  }

  function getStoredEmail() {
    try {
      return localStorage.getItem('vrvCheckoutEmail') || '';
    } catch (_) {
      return '';
    }
  }

  function extractPhoneFromEmail(email) {
    if (!email) return '';
    const match = String(email).trim().toLowerCase().match(/^(\+?91)?([6-9]\d{9})@/);
    return match ? match[2] : '';
  }

  function getIdentity(overrideOpts) {
    let phone = (overrideOpts && overrideOpts.phone) || '';
    let email = (overrideOpts && overrideOpts.email) || '';

    if (!phone || !email) {
      const body = document.body;
      const cfg = parseRzpConfig() || {};
      const loggedIn = isCustomerLoggedIn();

      phone = phone || (body && body.getAttribute('data-vrv-customer-phone')) || '';
      email = email || (body && body.getAttribute('data-vrv-customer-email')) || '';

      if (loggedIn) {
        phone = phone || cfg.phone || getStoredPhone() || '';
        email = email || cfg.email || getStoredEmail() || '';
        if (!phoneDigits(phone) && typeof window.vrvGetSessionPhone === 'function') {
          phone = window.vrvGetSessionPhone() || phone;
        }
        if (!String(email || '').trim() && typeof window.vrvGetSessionEmail === 'function') {
          email = window.vrvGetSessionEmail() || email;
        }
      }
    }

    if (!phoneDigits(phone) && email) {
      const extracted = extractPhoneFromEmail(email);
      if (extracted) phone = extracted;
    }

    return { phone, email };
  }

  function phoneDigits(phone) {
    const digits = String(phone || '').replace(/\D/g, '');
    return digits.length >= 10 ? digits.slice(-10) : digits;
  }

  function identityKeyFromIdentity(identity) {
    let phone10 = phoneDigits(identity && identity.phone);
    if (phone10.length !== 10 && identity && identity.email) {
      phone10 = extractPhoneFromEmail(identity.email);
    }
    if (phone10.length === 10) return 'phone:' + phone10;
    const email = String((identity && identity.email) || '')
      .trim()
      .toLowerCase();
    if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return 'email:' + email;
    return '';
  }

  function readActiveIdentityKey() {
    try {
      return localStorage.getItem(ACTIVE_IDENTITY_KEY) || '';
    } catch (_) {
      return '';
    }
  }

  function writeActiveIdentityKey(key) {
    try {
      if (key) localStorage.setItem(ACTIVE_IDENTITY_KEY, key);
      else localStorage.removeItem(ACTIVE_IDENTITY_KEY);
    } catch (_) {}
  }

  function resetLocalUserScopedData() {
    try {
      localStorage.setItem('wishlist', '[]');
      localStorage.removeItem(WISHLIST_TOMBSTONE_KEY);
      localStorage.removeItem(CART_TOMBSTONE_KEY);
      localStorage.removeItem(WISHLIST_CLEARED_KEY);
      localStorage.removeItem(CART_CLEARED_KEY);
    } catch (_) {}
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { source: 'vrv-user-sync' } }));
  }

  async function clearShopifyCart() {
    applyingRemote = true;
    try {
      const res = await fetch('/cart/clear.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          'X-VSW-Internal': '1',
        },
      });
      return res.ok;
    } catch (_) {
      return false;
    } finally {
      window.setTimeout(function () {
        applyingRemote = false;
      }, 400);
    }
  }

  async function prepareIdentitySession(identity) {
    const newKey = identityKeyFromIdentity(identity);
    if (!newKey) return false;
    const prevKey = readActiveIdentityKey();
    if (newKey === prevKey) return false;

    // If there was a previous logged-in user and the mobile number/email is different,
    // this is an account switch. Clear the previous account's cart and wishlist so they do not leak.
    if (prevKey && prevKey !== newKey) {
      discardBuyNowSavedCart();
      clearGuestCartSnapshot();
      resetLocalUserScopedData();
      await clearShopifyCart();
    } else {
      // Guest-to-user login transition — do NOT clear the cart or reset wishlist,
      // so that guest cart products can be merged with the user's account.
      clearCartUserClearedFlag();
    }

    writeActiveIdentityKey(newKey);
    return true;
  }

  async function prepareLogout() {
    try {
      if (hasIdentity()) {
        await syncWithServer({ pushOnly: true, skipRefresh: true });
      }
    } catch (_) {}
    discardBuyNowSavedCart();
    clearGuestCartSnapshot();
    resetLocalUserScopedData();
    writeActiveIdentityKey('');
    try {
      localStorage.removeItem('vrvCheckoutPhone');
      localStorage.removeItem('vrvCheckoutEmail');
    } catch (_) {}
    await clearShopifyCart();
  }

  function setRemoteWishlist(remote) {
    const items = (remote || [])
      .filter(function (item) {
        return item && item.handle;
      })
      .map(function (item) {
        return {
          handle: String(item.handle),
          variantId: String(item.variantId || item.variant_id || ''),
          title: item.title || '',
          url: item.url || '',
        };
      });
    localStorage.setItem('wishlist', JSON.stringify(items));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { source: 'vrv-user-sync' } }));
  }

  function hasIdentity(overrideOpts) {
    const id = getIdentity(overrideOpts);
    if (phoneDigits(id.phone).length === 10) return true;
    if (extractPhoneFromEmail(id.email).length === 10) return true;
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(id.email || '').trim());
  }

  // ─── Wishlist helpers ──────────────────────────────────────────────────────

  function readLocalWishlist() {
    const raw = readRawLocalWishlist();
    clearWishlistTombstonesForPresentItems(raw);
    return filterWishlistByTombstones(raw);
  }

  window.vrvReadLocalWishlist = readLocalWishlist;

  function mergeWishlistsByHandle(local, remote) {
    const map = new Map();
    [...local, ...(remote || [])].forEach((item) => {
      if (!item || !item.handle) return;
      map.set(String(item.handle), {
        handle: String(item.handle),
        variantId: String(item.variantId || item.variant_id || ''),
        title: item.title || '',
        url: item.url || '',
      });
    });
    return Array.from(map.values());
  }

  function wishlistsEqual(a, b) {
    return JSON.stringify(a) === JSON.stringify(b);
  }

  function applyRemoteWishlist(remote) {
    clearWishlistTombstonesForPresentItems(readRawLocalWishlist());
    const local = readLocalWishlist();
    const tombstones = readWishlistTombstones();
    const hasTombstones = Object.keys(tombstones).length > 0;
    let filteredRemote = (remote || []).filter(function (r) {
      return r && r.handle && !tombstones[r.handle];
    });
    // User cleared wishlist on this device — block ghost items from old server state.
    if (!local.length && (hasTombstones || isWishlistUserCleared())) {
      filteredRemote = [];
    }
    const merged = mergeWishlistsByHandle(local, filteredRemote);
    if (wishlistsEqual(local, merged)) return false;
    localStorage.setItem('wishlist', JSON.stringify(merged));
    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { source: 'vrv-user-sync' } }));
    return true;
  }

  // ─── Cart helpers ──────────────────────────────────────────────────────────

  async function fetchLocalCartItems() {
    const res = await fetch('/cart.js', {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    });
    if (!res.ok) return [];
    const cart = await res.json();
    return (cart.items || []).map((item) => ({
      variant_id: item.variant_id,
      quantity: item.quantity,
    }));
  }

  async function refreshCartUi(existingCart) {
    let cart = existingCart;
    if (!cart) {
      try {
        const cartRes = await fetch('/cart.js', {
          credentials: 'same-origin',
          headers: { Accept: 'application/json' },
        });
        if (cartRes.ok) cart = await cartRes.json();
      } catch (_) {}
    }
    if (!cart) return;

    if (typeof window.vrvInvalidateCartJsCache === 'function') {
      try { window.vrvInvalidateCartJsCache(); } catch (_) {}
    }

    document.dispatchEvent(
      new CustomEvent('cart:updated', {
        detail: Object.assign({}, cart, { source: 'vrv-user-sync' }),
      })
    );

    if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
      try {
        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'vrv-user-sync', cartData: cart });
      } catch (_) {}
    }

    // Update cart count bubble
    const bubble = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    if (bubble) {
      const visible = bubble.querySelector('span[aria-hidden="true"]');
      if (visible) visible.textContent = cart.item_count < 100 ? String(cart.item_count) : '99+';
      if (cart.item_count > 0) {
        bubble.hidden = false;
        bubble.removeAttribute('hidden');
        bubble.style.display = '';
      } else {
        bubble.hidden = true;
        bubble.style.display = 'none';
      }
    }

    // Refresh cart drawer
    const drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.classList.toggle('is-empty', cart.item_count === 0);
      const countEl = drawer.querySelector('.vrv-drawer__count');
      if (countEl) {
        countEl.textContent = '(' + cart.item_count + ' ' + (cart.item_count === 1 ? 'item' : 'items') + ')';
      }
      if (typeof drawer.refreshDrawer === 'function') {
        try { await drawer.refreshDrawer(true); } catch (_) {}
      }
    }

    if (typeof window.vrvSyncCartDrawerFromServer === 'function') {
      try { await window.vrvSyncCartDrawerFromServer({ cart: cart, preserveBubble: false }); } catch (_) {}
    }

    const cartItemsEl = document.querySelector('cart-items');
    if (cartItemsEl && typeof cartItemsEl.onCartUpdate === 'function') {
      try { cartItemsEl.onCartUpdate(); } catch (_) {}
    }

    document.dispatchEvent(new CustomEvent('wishlist:updated', { detail: { source: 'vrv-user-sync' } }));
  }

  async function mergeRemoteCart(remoteItems, isLoginMerge) {
    const cartTombstones = readCartTombstones();
    const hasTombstones = Object.keys(cartTombstones).length > 0;
    let items = isLoginMerge
      ? filterCartItemsByTombstones(Array.isArray(remoteItems) ? remoteItems : [], cartTombstones)
      : (Array.isArray(remoteItems) ? remoteItems : []);

    let localCart = null;
    try {
      const cartRes = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (cartRes.ok) localCart = await cartRes.json();
    } catch (_) {
      return false;
    }

    // Safety net: if localCart is empty in Shopify, but guest snapshot had items during login merge, restore them first
    if ((!localCart || !localCart.items || !localCart.items.length) && isLoginMerge) {
      const guestSnapshot = readGuestCartSnapshot();
      if (guestSnapshot.length) {
        const restoreItems = guestSnapshot
          .map(function (it) {
            return { id: Number(it.variant_id), quantity: Math.max(1, Number(it.quantity) || 1) };
          })
          .filter(function (it) { return it.id > 0; });
        if (restoreItems.length) {
          try {
            await fetch('/cart/add.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ items: restoreItems }),
            });
            const refCart = await fetch('/cart.js', {
              credentials: 'same-origin',
              headers: { Accept: 'application/json' },
            });
            if (refCart.ok) localCart = await refCart.json();
          } catch (_) {}
        }
      }
    }

    if (isLoginMerge) {
      clearGuestCartSnapshot();
    }

    if (!localCart) return false;

    const localEmpty = !(localCart.items || []).length;
    // Routine sync (not login merge): if user explicitly cleared cart on this device, do not resurrect old items
    if (!isLoginMerge && localEmpty && (hasTombstones || isCartUserCleared())) return false;

    const localItems = localCart.items || [];
    const localMap = new Map();
    localItems.forEach(function (line) {
      const vid = Number(line.variant_id);
      if (vid > 0) {
        localMap.set(vid, {
          variant_id: vid,
          quantity: Number(line.quantity) || 1,
          key: line.key,
        });
      }
    });

    const itemsToAdd = [];
    const updates = {};
    let hasChanges = false;

    // Remote map to dedupe remote items
    const remoteMap = new Map();
    items.forEach(function (item) {
      const vid = Number(item.variant_id || item.variantId);
      const qty = Math.max(1, Math.min(99, Number(item.quantity) || 1));
      if (!vid || Number.isNaN(vid)) return;
      const prev = remoteMap.get(vid);
      remoteMap.set(vid, prev ? Math.max(prev, qty) : qty);
    });

    if (isLoginMerge) {
      // Login merge: combine guest items with account items
      remoteMap.forEach(function (remoteQty, vid) {
        if (localMap.has(vid)) {
          const localItem = localMap.get(vid);
          const targetQty = Math.min(99, localItem.quantity + remoteQty);
          if (targetQty !== localItem.quantity) {
            updates[String(vid)] = targetQty;
            hasChanges = true;
          }
        } else {
          itemsToAdd.push({ id: vid, quantity: remoteQty });
          hasChanges = true;
        }
      });
    } else {
      // Routine sync for logged-in sessions: remote account cart is authoritative!
      // 1. Items in remote: update quantity or add if missing
      remoteMap.forEach(function (remoteQty, vid) {
        if (localMap.has(vid)) {
          const localItem = localMap.get(vid);
          if (localItem.quantity !== remoteQty) {
            updates[String(vid)] = remoteQty;
            hasChanges = true;
          }
        } else {
          itemsToAdd.push({ id: vid, quantity: remoteQty });
          hasChanges = true;
        }
      });

      // While the shopper is actively editing the bag (e.g. removing items one by
      // one) a remote snapshot is always at least one step behind, so it must not
      // be allowed to delete lines or clear the cart; it is re-evaluated on the
      // next sync once the edits settle.
      const recentLocalEdit = Date.now() - lastLocalMutationAt < 10000;

      // 2. Items in local but NOT in remote: remove them (quantity = 0)
      localMap.forEach(function (localItem, vid) {
        if (!remoteMap.has(vid) && !recentLocalEdit) {
          updates[String(vid)] = 0;
          hasChanges = true;
        }
      });

      // 3. If remote is completely empty and local had items: clear cart
      if (!remoteMap.size && localMap.size && !recentLocalEdit) {
        await clearShopifyCart();
        if (typeof window.vrvInvalidateCartJsCache === 'function') {
          try { window.vrvInvalidateCartJsCache(); } catch (_) {}
        }
        await refreshCartUi({ items: [], item_count: 0 });
        return true;
      }
    }

    if (!hasChanges) {
      await refreshCartUi(localCart);
      return false;
    }

    applyingRemote = true;
    try {
      // 1. Update quantities / remove items via /cart/update.js
      if (Object.keys(updates).length > 0) {
        try {
          await fetch('/cart/update.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ updates: updates }),
          });
        } catch (_) {}
      }

      // 2. Add remote items not yet in cart via /cart/add.js
      if (itemsToAdd.length > 0) {
        let batchSuccess = false;
        try {
          const res = await fetch('/cart/add.js', {
            method: 'POST',
            credentials: 'same-origin',
            headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
            body: JSON.stringify({ items: itemsToAdd }),
          });
          if (res.ok) {
            batchSuccess = true;
          }
        } catch (_) {}

        if (!batchSuccess) {
          for (const item of itemsToAdd) {
            try {
              await fetch('/cart/add.js', {
                method: 'POST',
                credentials: 'same-origin',
                headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
                body: JSON.stringify({ id: item.id, quantity: item.quantity }),
              });
            } catch (_) {}
          }
        }
      }

      if (typeof window.vrvInvalidateCartJsCache === 'function') {
        try { window.vrvInvalidateCartJsCache(); } catch (_) {}
      }

      const freshCartRes = await fetch('/cart.js', {
        credentials: 'same-origin',
        headers: { Accept: 'application/json' },
      });
      if (freshCartRes.ok) {
        const freshCart = await freshCartRes.json();
        await refreshCartUi(freshCart);
      }
      return true;
    } finally {
      window.setTimeout(function () {
        applyingRemote = false;
      }, 400);
    }
  }

  // ─── Sync ──────────────────────────────────────────────────────────────────

  async function syncWithServer(options) {
    const opts = options || {};
    if (!hasIdentity(opts)) return null;
    const api = getApiUrl();
    if (!api) return null;
    if (isBuyNowCartSwapPending()) return null;

    if (syncInFlight) return syncInFlight;

    const identity = getIdentity(opts);
    const identitySwitched = await prepareIdentitySession(identity);
    const isLoginMerge = Boolean(opts.isLoginMerge || identitySwitched);
    const isPush = Boolean(opts.isPush || opts.pushOnly);
    const syncGenAtStart = localMutationGen;
    let cartMergedIn = false;
    syncInFlight = (async function () {
      purgeGhostWishlistFromStorage();

      const payload = await buildSyncPayloadAsync(identity, isLoginMerge, isPush);
      const wishlistTombstones = pruneTombstones(readWishlistTombstones());
      const deletedWishlistHandles = Object.keys(wishlistTombstones);

      const res = await fetch(api, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });

      if (!res.ok) return null;
      const data = await res.json();
      if (!data || !data.ok) return null;

      // Push-only sync (after delete or local update): update server, never apply remote response.
      if (opts.pushOnly) return data;

      // If the user changed cart/wishlist while this sync was in flight (and not a login merge),
      // do not apply a stale server response that could overwrite recent local changes.
      if (!opts.forceApply && !isLoginMerge && syncGenAtStart !== localMutationGen) return data;

      const remoteWishlist = filterWishlistByTombstones(
        data.wishlist || data.priorWishlist || [],
        wishlistTombstones
      );
      const remoteCartItems = (data.cart && Array.isArray(data.cart.items))
        ? data.cart.items
        : ((data.priorCart && Array.isArray(data.priorCart.items)) ? data.priorCart.items : []);

      const clearedWishlist =
        !payload.wishlist.length &&
        (deletedWishlistHandles.length > 0 || isWishlistUserCleared() || payload.wishlist_cleared);

      applyRemoteWishlist(clearedWishlist ? [] : remoteWishlist);
      cartMergedIn = await mergeRemoteCart(remoteCartItems, isLoginMerge);
      if (!opts.skipRefresh) await refreshCartUi();
      return data;
    })();

    try {
      return await syncInFlight;
    } finally {
      syncInFlight = null;
      // On login merge, push the newly combined cart back to server so all devices see the union.
      if (cartMergedIn && isLoginMerge && !opts.pushOnly) {
        setTimeout(function () {
          syncWithServer({ isPush: true, pushOnly: true }).catch(function () {});
        }, 0);
      }
    }
  }

  async function flushDeleteSync() {
    if (!hasIdentity()) return null;
    try {
      return await syncWithServer({ pushOnly: true, skipRefresh: true });
    } catch (_) {
      return null;
    }
  }

  function beaconPushOnExit() {
    if (!hasIdentity()) return;
    if (isBuyNowCartSwapPending()) return;
    const api = getApiUrl();
    if (!api || !navigator.sendBeacon) return;
    try {
      const identity = getIdentity();
      const payload = buildSyncPayload(identity);
      fetchLocalCartItems()
        .then(function (items) {
          clearCartTombstonesForPresentItems(items);
          payload.cart.items = filterCartItemsByTombstones(
            items,
            pruneTombstones(readCartTombstones())
          );
          const cartTombstones = pruneTombstones(readCartTombstones());
          const deletedCartVariantIds = Object.keys(cartTombstones)
            .map(Number)
            .filter(function (n) { return n > 0 && !isNaN(n); });
          payload.cart_cleared =
            !payload.cart.items.length && (isCartUserCleared() || deletedCartVariantIds.length > 0);
          navigator.sendBeacon(api, JSON.stringify(payload));
        })
        .catch(function () {
          navigator.sendBeacon(api, JSON.stringify(payload));
        });
    } catch (_) {}
  }

  function schedulePush() {
    if (!hasIdentity() || applyingRemote) return;
    if (pushTimer) window.clearTimeout(pushTimer);
    pushTimer = window.setTimeout(function () {
      pushTimer = null;
      syncWithServer({ isPush: true }).catch(function () {});
    }, DEBOUNCE_MS);
  }

  function schedulePushImmediate() {
    if (!hasIdentity() || applyingRemote) return;
    if (pushTimer) window.clearTimeout(pushTimer);
    pushTimer = null;
    syncWithServer({ isPush: true }).catch(function () {});
  }

  function watchIdentityChanges() {
    if (!document.body) return;
    const observer = new MutationObserver(function () {
      if (hasIdentity()) {
        syncWithServer().catch(function () {});
      }
    });
    observer.observe(document.body, {
      attributes: true,
      attributeFilter: ['data-vrv-customer-phone', 'data-vrv-customer-email', 'class'],
    });
  }

  function boot() {
    purgeGhostWishlistFromStorage();
    watchIdentityChanges();
    if (!isCustomerLoggedIn()) {
      // Snapshot refresh is not needed for first paint -- run it when the
      // browser is idle instead of competing with page load.
      const snapshotGuestCart = function () {
        fetchLocalCartItems().then(function (items) {
          if (items && items.length) writeGuestCartSnapshot(items);
        }).catch(function () {});
      };
      if (typeof window.requestIdleCallback === 'function') {
        window.requestIdleCallback(snapshotGuestCart, { timeout: 3000 });
      } else {
        window.setTimeout(snapshotGuestCart, 1500);
      }
    }
    if (hasIdentity()) {
      syncWithServer().catch(function () {});
    } else {
      let tries = 0;
      const retry = window.setInterval(function () {
        tries += 1;
        if (hasIdentity()) {
          window.clearInterval(retry);
          syncWithServer().catch(function () {});
        } else if (tries >= 20) {
          window.clearInterval(retry);
        }
      }, 500);
    }
  }

  function onLocalCartWishlistChange(event) {
    if (event && event.detail && event.detail.source === 'vrv-user-sync') return;
    // Drawer re-renders don't change the cart or wishlist. Treating them as a
    // change also wiped the guest cart snapshot (their detail carries no items).
    if (event && event.detail && (event.detail.source === 'refreshDrawer' || event.detail.source === 'vrv-partial-refresh')) return;
    clearCartUserClearedFlag();
    lastLocalCartMutationAt = Date.now();
    bumpLocalMutationGen();

    if (!isCustomerLoggedIn()) {
      if (event && event.detail) {
        updateGuestCartSnapshotFromCart(event.detail);
      } else {
        fetchLocalCartItems().then(function (items) {
          writeGuestCartSnapshot(items);
        }).catch(function () {});
      }
    } else {
      schedulePush();
    }
  }

  document.addEventListener('wishlist:updated', onLocalCartWishlistChange);
  document.addEventListener('cart:updated', onLocalCartWishlistChange);

  // Returning to the tab fires both visibilitychange and focus, and each run
  // costs a worker POST plus several /cart.js calls and a drawer refresh.
  // Coalesce them and don't re-sync more than once per RETURN_SYNC_MIN_MS.
  let lastReturnSyncAt = 0;
  function syncOnReturn() {
    if (!hasIdentity()) return;
    const now = Date.now();
    if (now - lastReturnSyncAt < RETURN_SYNC_MIN_MS) return;
    lastReturnSyncAt = now;
    syncWithServer().catch(function () {});
  }

  function syncOnDrawerOpen() {
    if (!hasIdentity() || !isCustomerLoggedIn() || applyingRemote) return;
    const now = Date.now();
    if (now - lastReturnSyncAt < 2000) return;
    lastReturnSyncAt = now;
    syncWithServer().catch(function () {});
  }

  document.addEventListener('visibilitychange', function () {
    if (!document.hidden) syncOnReturn();
  });

  window.addEventListener('focus', syncOnReturn);
  document.addEventListener('cart-drawer:open', syncOnDrawerOpen);

  window.addEventListener('pagehide', beaconPushOnExit);

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  window.vrvUserSync = {
    sync: syncWithServer,
    push: schedulePush,
    syncOnDrawerOpen: syncOnDrawerOpen,
    flushDeleteSync: flushDeleteSync,
    hasIdentity: hasIdentity,
    isLoggedIn: isCustomerLoggedIn,
    prepareLogout: prepareLogout,
    markWishlistUserCleared: markWishlistUserCleared,
    markCartUserCleared: markCartUserCleared,
    clearWishlistUserClearedFlag: clearWishlistUserClearedFlag,
    clearCartUserClearedFlag: clearCartUserClearedFlag,
    readGuestCartSnapshot: readGuestCartSnapshot,
    clearGuestCartSnapshot: clearGuestCartSnapshot,
  };
})();
